import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { appointmentsAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  Loader2,
  Trash2,
  Pencil,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  ChevronsUpDown,
} from 'lucide-react';

const CalendarPage = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'list'
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    client_id: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    start_datetime: '',
    duration_hours: 1, // Durée en heures
    end_datetime: '',
    location: '',
    appointment_type: 'rdv',
    status: 'planifie',
    notes: '',
    color: '#f59e0b',
  });

  // Calculate end_datetime when start_datetime or duration changes
  const updateEndDateTime = (startDateTime, durationHours) => {
    if (startDateTime && durationHours) {
      const start = new Date(startDateTime);
      const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
      return end.toISOString().slice(0, 16);
    }
    return '';
  };

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients;
    const term = clientSearchTerm.toLowerCase();
    return clients.filter(c => 
      c.name?.toLowerCase().includes(term) ||
      c.company?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.includes(term)
    );
  }, [clients, clientSearchTerm]);

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      // Get appointments for the current month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const [appointmentsRes, clientsRes] = await Promise.all([
        appointmentsAPI.getAll({
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString(),
        }),
        clientsAPI.getAll(),
      ]);
      
      setAppointments(appointmentsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      // Get client name if client selected
      let clientName = form.contact_name;
      if (form.client_id) {
        const selectedClient = clients.find(c => c.id === form.client_id);
        if (selectedClient) {
          clientName = selectedClient.name;
        }
      }
      
      const data = {
        ...form,
        client_name: clientName,
      };
      
      if (editingAppointment) {
        await appointmentsAPI.update(editingAppointment.id, data);
        toast.success('Rendez-vous mis à jour');
      } else {
        await appointmentsAPI.create(data);
        toast.success('Rendez-vous créé');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    // Calculate duration from start and end times
    let durationHours = 1;
    if (appointment.start_datetime && appointment.end_datetime) {
      const start = new Date(appointment.start_datetime);
      const end = new Date(appointment.end_datetime);
      durationHours = (end - start) / (1000 * 60 * 60);
    }
    setForm({
      title: appointment.title || '',
      description: appointment.description || '',
      client_id: appointment.client_id || '',
      contact_name: appointment.contact_name || '',
      contact_phone: appointment.contact_phone || '',
      contact_email: appointment.contact_email || '',
      start_datetime: appointment.start_datetime || '',
      duration_hours: durationHours,
      end_datetime: appointment.end_datetime || '',
      location: appointment.location || '',
      appointment_type: appointment.appointment_type || 'rdv',
      status: appointment.status || 'planifie',
      notes: appointment.notes || '',
      color: appointment.color || '#f59e0b',
    });
    setDetailDialogOpen(false);
    setDialogOpen(true);
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    
    try {
      await appointmentsAPI.delete(appointmentId);
      toast.success('Rendez-vous supprimé');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentsAPI.updateStatus(appointmentId, newStatus);
      toast.success('Statut mis à jour');
      fetchData();
      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSendReminder = async (appointmentId) => {
    setFormLoading(true);
    try {
      const response = await appointmentsAPI.sendReminder(appointmentId);
      toast.success(response.data?.message || 'Rappel envoyé');
      fetchData();
      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment(prev => ({ ...prev, reminder_sent: true }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du rappel');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setEditingAppointment(null);
    setClientSearchTerm('');
    setForm({
      title: '',
      description: '',
      client_id: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      start_datetime: '',
      duration_hours: 1,
      end_datetime: '',
      location: '',
      appointment_type: 'rdv',
      status: 'planifie',
      notes: '',
      color: '#f59e0b',
    });
  };

  const openNewAppointment = (date) => {
    const dateStr = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0).toISOString().slice(0, 16) : '';
    const endStr = dateStr ? new Date(new Date(dateStr).getTime() + 1 * 60 * 60 * 1000).toISOString().slice(0, 16) : '';
    setForm(prev => ({
      ...prev,
      start_datetime: dateStr,
      duration_hours: 1,
      end_datetime: endStr,
    }));
    setDialogOpen(true);
  };

  const viewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setDetailDialogOpen(true);
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        currentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i),
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        currentMonth: true,
        date: new Date(year, month, i),
      });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }
    
    return days;
  }, [currentDate]);

  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.start_datetime);
      return aptDate.getFullYear() === date.getFullYear() &&
             aptDate.getMonth() === date.getMonth() &&
             aptDate.getDate() === date.getDate();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      planifie: { label: 'Planifié', className: 'bg-blue-100 text-blue-700' },
      confirme: { label: 'Confirmé', className: 'bg-green-100 text-green-700' },
      annule: { label: 'Annulé', className: 'bg-red-100 text-red-700' },
      termine: { label: 'Terminé', className: 'bg-slate-100 text-slate-700' },
    };
    const s = statusMap[status] || statusMap.planifie;
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  const getTypeLabel = (type) => {
    const types = {
      rdv: 'Rendez-vous',
      demo: 'Démonstration',
      visite: 'Visite',
      appel: 'Appel',
      autre: 'Autre',
    };
    return types[type] || type;
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Calendrier
          </h1>
          <p className="text-slate-500 mt-1">{appointments.length} rendez-vous ce mois</p>
        </div>
        
        <Button 
          className="bg-orange-500 hover:bg-orange-600"
          onClick={() => openNewAppointment(null)}
          data-testid="new-appointment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau rendez-vous
        </Button>
      </div>

      {/* Calendar Navigation */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Aujourd'hui
              </Button>
            </div>
            
            <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            
            <div className="flex items-center gap-2">
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mois</SelectItem>
                  <SelectItem value="list">Liste</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      {viewMode === 'month' && (
        <Card className="border-slate-200">
          <CardContent className="p-2 sm:p-4">
            {/* Day names header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-semibold text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayInfo, index) => {
                const dayAppointments = getAppointmentsForDate(dayInfo.date);
                const isToday = dayInfo.date.toDateString() === new Date().toDateString();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] sm:min-h-[100px] p-1 border rounded-md cursor-pointer transition-colors hover:bg-slate-50
                      ${dayInfo.currentMonth ? 'bg-white' : 'bg-slate-50'}
                      ${isToday ? 'border-orange-400 border-2' : 'border-slate-200'}
                    `}
                    onClick={() => openNewAppointment(dayInfo.date)}
                    data-testid={`calendar-day-${dayInfo.day}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${dayInfo.currentMonth ? 'text-slate-800' : 'text-slate-400'} ${isToday ? 'text-orange-600' : ''}`}>
                      {dayInfo.day}
                    </div>
                    
                    <div className="space-y-1">
                      {dayAppointments.slice(0, 2).map(apt => (
                        <div
                          key={apt.id}
                          className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                          style={{ backgroundColor: apt.color || '#f59e0b', color: '#fff' }}
                          onClick={(e) => { e.stopPropagation(); viewAppointment(apt); }}
                          data-testid={`appointment-${apt.id}`}
                        >
                          {formatTime(apt.start_datetime)} {apt.title}
                        </div>
                      ))}
                      {dayAppointments.length > 2 && (
                        <div className="text-xs text-slate-500 text-center">
                          +{dayAppointments.length - 2} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {appointments.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Aucun rendez-vous ce mois
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {appointments.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)).map(apt => (
                  <div
                    key={apt.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => viewAppointment(apt)}
                    data-testid={`appointment-list-${apt.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div
                          className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                          style={{ backgroundColor: apt.color || '#f59e0b' }}
                        />
                        <div>
                          <p className="font-medium text-slate-800">{apt.title}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(apt.start_datetime)}
                          </p>
                          <p className="text-sm text-slate-500 flex items-center gap-2">
                            <Clock className="w-3 h-3" />
                            {formatTime(apt.start_datetime)} - {formatTime(apt.end_datetime)}
                          </p>
                          {apt.client_name && (
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {apt.client_name}
                            </p>
                          )}
                          {apt.location && (
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              {apt.location}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(apt.status)}
                        <Badge variant="outline" className="text-xs">
                          {getTypeLabel(apt.appointment_type)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* New/Edit Appointment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingAppointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Visite client, Démo machine..."
                required
                data-testid="appointment-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appointment_type">Type</Label>
                <Select value={form.appointment_type} onValueChange={(v) => setForm({ ...form, appointment_type: v })}>
                  <SelectTrigger data-testid="appointment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rdv">Rendez-vous</SelectItem>
                    <SelectItem value="demo">Démonstration</SelectItem>
                    <SelectItem value="visite">Visite</SelectItem>
                    <SelectItem value="appel">Appel</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="color">Couleur</Label>
                <Input
                  id="color"
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-10 p-1"
                  data-testid="appointment-color-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_datetime">Début *</Label>
                <Input
                  id="start_datetime"
                  type="datetime-local"
                  value={form.start_datetime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    const newEnd = updateEndDateTime(newStart, form.duration_hours);
                    setForm({ ...form, start_datetime: newStart, end_datetime: newEnd });
                  }}
                  required
                  data-testid="appointment-start-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="duration_hours">Durée (heures)</Label>
                <Select 
                  value={String(form.duration_hours)} 
                  onValueChange={(v) => {
                    const duration = parseFloat(v);
                    const newEnd = updateEndDateTime(form.start_datetime, duration);
                    setForm({ ...form, duration_hours: duration, end_datetime: newEnd });
                  }}
                >
                  <SelectTrigger data-testid="appointment-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">30 min</SelectItem>
                    <SelectItem value="1">1 heure</SelectItem>
                    <SelectItem value="1.5">1h30</SelectItem>
                    <SelectItem value="2">2 heures</SelectItem>
                    <SelectItem value="2.5">2h30</SelectItem>
                    <SelectItem value="3">3 heures</SelectItem>
                    <SelectItem value="4">4 heures</SelectItem>
                    <SelectItem value="5">5 heures</SelectItem>
                    <SelectItem value="6">6 heures</SelectItem>
                    <SelectItem value="8">8 heures</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.end_datetime && (
              <div className="text-sm text-slate-500 flex items-center gap-2 -mt-2">
                <Clock className="w-4 h-4" />
                Fin prévue : {new Date(form.end_datetime).toLocaleString('fr-FR', { 
                  weekday: 'short', 
                  day: 'numeric', 
                  month: 'short',
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Client (rechercher)</Label>
              <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={clientSearchOpen}
                    className="w-full justify-between"
                    data-testid="appointment-client-search"
                  >
                    {form.client_id 
                      ? clients.find(c => c.id === form.client_id)?.name || "Client sélectionné"
                      : "Rechercher un client..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput 
                      placeholder="Rechercher par nom, email, téléphone..." 
                      value={clientSearchTerm}
                      onValueChange={setClientSearchTerm}
                      data-testid="client-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>Aucun client trouvé</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="none"
                          onSelect={() => {
                            setForm({ ...form, client_id: '' });
                            setClientSearchOpen(false);
                            setClientSearchTerm('');
                          }}
                        >
                          <span className="text-slate-500">Aucun client</span>
                        </CommandItem>
                        {filteredClients.slice(0, 10).map(client => (
                          <CommandItem
                            key={client.id}
                            value={client.id}
                            onSelect={() => {
                              setForm({ 
                                ...form, 
                                client_id: client.id,
                                contact_phone: client.phone || form.contact_phone,
                                contact_email: client.email || form.contact_email,
                              });
                              setClientSearchOpen(false);
                              setClientSearchTerm('');
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{client.name}</span>
                              <span className="text-xs text-slate-500">
                                {client.phone && `📞 ${client.phone}`}
                                {client.phone && client.email && ' • '}
                                {client.email && `✉️ ${client.email}`}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                        {filteredClients.length > 10 && (
                          <CommandItem disabled>
                            <span className="text-xs text-slate-400">
                              +{filteredClients.length - 10} autres résultats...
                            </span>
                          </CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {!form.client_id && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nom du contact</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Nom du contact"
                    data-testid="appointment-contact-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Téléphone</Label>
                    <Input
                      id="contact_phone"
                      value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      placeholder="Téléphone"
                      data-testid="appointment-contact-phone-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                      placeholder="Email"
                      data-testid="appointment-contact-email-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="location">Lieu</Label>
              <Input
                id="location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Adresse ou lieu du rendez-vous"
                data-testid="appointment-location-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Notes supplémentaires..."
                data-testid="appointment-notes-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-slate-800 hover:bg-slate-900"
                disabled={formLoading}
                data-testid="save-appointment-btn"
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingAppointment ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Détails du rendez-vous
            </DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: selectedAppointment.color || '#f59e0b' }}
                />
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">{selectedAppointment.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(selectedAppointment.status)}
                    <Badge variant="outline">{getTypeLabel(selectedAppointment.appointment_type)}</Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <CalendarIcon className="w-4 h-4" />
                  {formatDate(selectedAppointment.start_datetime)}
                </div>
                
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-4 h-4" />
                  {formatTime(selectedAppointment.start_datetime)} - {formatTime(selectedAppointment.end_datetime)}
                </div>
                
                {(selectedAppointment.client_name || selectedAppointment.contact_name) && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <User className="w-4 h-4" />
                    {selectedAppointment.client_name || selectedAppointment.contact_name}
                  </div>
                )}
                
                {selectedAppointment.contact_phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${selectedAppointment.contact_phone}`} className="hover:text-blue-600">
                      {selectedAppointment.contact_phone}
                    </a>
                  </div>
                )}
                
                {selectedAppointment.contact_email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${selectedAppointment.contact_email}`} className="hover:text-blue-600">
                      {selectedAppointment.contact_email}
                    </a>
                  </div>
                )}
                
                {selectedAppointment.location && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    {selectedAppointment.location}
                  </div>
                )}
                
                {selectedAppointment.notes && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-slate-700">{selectedAppointment.notes}</p>
                  </div>
                )}
                
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Changer le statut</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedAppointment.status === 'planifie' ? 'default' : 'outline'}
                      className={selectedAppointment.status === 'planifie' ? 'bg-blue-500' : ''}
                      onClick={() => handleStatusChange(selectedAppointment.id, 'planifie')}
                    >
                      Planifié
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAppointment.status === 'confirme' ? 'default' : 'outline'}
                      className={selectedAppointment.status === 'confirme' ? 'bg-green-500' : ''}
                      onClick={() => handleStatusChange(selectedAppointment.id, 'confirme')}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Confirmé
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAppointment.status === 'termine' ? 'default' : 'outline'}
                      className={selectedAppointment.status === 'termine' ? 'bg-slate-500' : ''}
                      onClick={() => handleStatusChange(selectedAppointment.id, 'termine')}
                    >
                      Terminé
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAppointment.status === 'annule' ? 'default' : 'outline'}
                      className={selectedAppointment.status === 'annule' ? 'bg-red-500' : ''}
                      onClick={() => handleStatusChange(selectedAppointment.id, 'annule')}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Annulé
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-slate-100">
                {(selectedAppointment.contact_email || selectedAppointment.client_id) && !selectedAppointment.reminder_sent && (
                  <Button
                    variant="outline"
                    className="text-blue-500 hover:text-blue-600"
                    onClick={() => handleSendReminder(selectedAppointment.id)}
                    disabled={formLoading}
                    data-testid="send-reminder-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Envoyer rappel
                  </Button>
                )}
                {selectedAppointment.reminder_sent && (
                  <Badge variant="outline" className="text-green-600 border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Rappel envoyé
                  </Badge>
                )}
                <Button
                  variant="outline"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => handleDelete(selectedAppointment.id)}
                  data-testid="delete-appointment-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleEdit(selectedAppointment)}
                  data-testid="edit-appointment-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
